import { db, type ReportRow, type SyncMetadata } from '../db';
import { Report as ReportSchema } from '../schemas';
import type { Report } from '../schemas';
import { AbstractBaseRepo } from './baseRepo';
import { createSyncMetadata, updateSyncMetadata } from '../sync/syncMetadata';
import { queueForSync } from '../sync/syncOutbox';
import { normalizeReportScheduling } from '../reports/reportScheduling';

export type ReportSummary = {
  id: string;
  title: string;
  visitDate: string;
  siteName: string;
  siteId: string;
  nonConformitiesOpen: number;
  nonConformitiesTotal: number;
  companyId: string;
  status: Report['status'];
  createdAt: string;
  updatedAt: string;
  finalizedAt?: string;
  companyName?: string;
};

export type ReportLockOwner = {
  deviceId: string;
  label?: string;
};

export type ReportLockState = {
  lockedBy?: string;
  lockedAt?: string;
  lockedDevice?: string;
  isLocked: boolean;
  isExpired: boolean;
  expiresAt?: string;
};

export type ReportLockResult =
  | { status: 'acquired'; lock: ReportLockState }
  | { status: 'locked'; lock: ReportLockState };

const REPORT_LOCK_TTL_MS = 30 * 60 * 1000;

/**
 * Serialize report payload for storage (JSON string).
 */
function toPayload(report: Report): string {
  return JSON.stringify(report);
}

/**
 * Serialize report row for sync outbox (payload is already a string).
 */
function serializeReportRowForSync(row: ReportRow): Record<string, unknown> {
  return { ...row };
}

function parseLockTimestamp(value?: string): number | null {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

function buildLockState(row: ReportRow): ReportLockState {
  const lockedAtMs = parseLockTimestamp(row.lockedAt);
  const isExpired = lockedAtMs !== null ? Date.now() - lockedAtMs > REPORT_LOCK_TTL_MS : false;
  const expiresAt =
    lockedAtMs !== null ? new Date(lockedAtMs + REPORT_LOCK_TTL_MS).toISOString() : undefined;

  return {
    lockedBy: row.lockedBy,
    lockedAt: row.lockedAt,
    lockedDevice: row.lockedDevice,
    isLocked: Boolean(row.lockedAt && !isExpired),
    isExpired,
    expiresAt,
  };
}

/**
 * Check if a report has valid format
 */
function isReport(payload: unknown): boolean {
  if (!payload || typeof payload !== 'object') return false;
  const obj = payload as Record<string, unknown>;
  return obj.version === 4 && 'data' in obj && typeof obj.data === 'object';
}

/**
 * Parse report row from database (payload is now a JSON string).
 * Handles legacy Blob format gracefully by throwing a helpful error.
 */
function parseReportRow(row: ReportRow): Report {
  // Cast to unknown first to handle legacy Blob payloads that TypeScript doesn't know about
  const rawPayload: unknown = row.payload;

  // Guard against legacy Blob payloads that haven't been migrated yet
  if (rawPayload instanceof Blob) {
    throw new Error(
      `Report ${row.id} has legacy Blob payload. Please refresh the page to trigger migration.`
    );
  }

  if (typeof rawPayload !== 'string') {
    throw new Error(`Report ${row.id} has invalid payload type: ${typeof rawPayload}`);
  }

  const payload = JSON.parse(rawPayload);

  if (!isReport(payload)) {
    throw new Error(`Report ${row.id} has invalid format.`);
  }

  return normalizeReportScheduling(ReportSchema.parse(payload));
}

function rowToSummary(entry: ReportRow): ReportSummary {
  const report = parseReportRow(entry);

  const nonConformities = report.data.nonConformities;
  const open = nonConformities.filter((item) => item.status !== 'closed').length;
  const companyName = report.meta.company;
  const status = report.status;

  return {
    id: report.id,
    title: report.meta.title,
    visitDate: report.meta.visitDate,
    siteName: report.meta.siteName ?? '',
    siteId: report.siteId,
    nonConformitiesOpen: open,
    nonConformitiesTotal: nonConformities.length,
    companyId: report.companyId,
    status,
    createdAt: report.createdAt,
    updatedAt: report.updatedAt,
    finalizedAt: report.finalizedAt,
    companyName,
  };
}

class ReportsRepoImpl extends AbstractBaseRepo<ReportSummary, Report, ReportRow, Report> {
  protected readonly table = db.reports;

  protected getRowId(row: ReportRow): string {
    return row.id;
  }

  protected getInputId(input: Report): string | null {
    return input.id;
  }

  protected toListModel(row: ReportRow): ReportSummary {
    return rowToSummary(row);
  }

  protected toGetModel(row: ReportRow): Report {
    return parseReportRow(row);
  }

  protected toRow(input: Report, ctx: { now: string; existing?: ReportRow }): ReportRow {
    const normalized = normalizeReportScheduling(ReportSchema.parse(input));
    // Sync metadata - will be overwritten by baseRepo.save()
    const syncMeta = ctx.existing
      ? {
          syncUpdatedAt: ctx.existing.syncUpdatedAt,
          syncDeletedAt: ctx.existing.syncDeletedAt,
          syncDeviceId: ctx.existing.syncDeviceId,
          syncVersion: ctx.existing.syncVersion,
        }
      : createSyncMetadata();

    return {
      id: normalized.id,
      version: 4,
      companyId: normalized.companyId,
      siteId: normalized.siteId,
      payload: toPayload(normalized),
      ...syncMeta,
    };
  }

  protected override async serializeOutboxPayload(row: ReportRow): Promise<unknown> {
    return serializeReportRowForSync(row);
  }

  override async get(id: string): Promise<Report> {
    const report = await super.get(id);
    if (!report) throw new Error('not found');
    if (!isReport(report)) {
      throw new Error(`Report ${id} has invalid format.`);
    }
    return report;
  }

  /**
   * Save or update a report
   */
  async upsert(report: Report): Promise<void> {
    await this.save(report);
  }

  async getLockState(reportId: string): Promise<ReportLockState | null> {
    const row = await this.table.get(reportId);
    if (!row) return null;
    return buildLockState(row);
  }

  async acquireLock(reportId: string, owner: ReportLockOwner): Promise<ReportLockResult> {
    const row = await this.table.get(reportId);
    if (!row) {
      throw new Error('Report non trovato');
    }

    const lockState = buildLockState(row);
    const isOwnedByRequester =
      row.lockedDevice === owner.deviceId || (owner.label && row.lockedBy === owner.label);

    if (lockState.isLocked && !isOwnedByRequester) {
      return { status: 'locked', lock: lockState };
    }

    const now = new Date().toISOString();
    const syncMeta = updateSyncMetadata(row as SyncMetadata);
    const nextRow: ReportRow = {
      ...row,
      ...syncMeta,
      lockedBy: owner.label ?? row.lockedBy ?? owner.deviceId,
      lockedAt: now,
      lockedDevice: owner.deviceId,
    };

    await this.table.put(nextRow);
    await queueForSync('reports', reportId, 'upsert', serializeReportRowForSync(nextRow));

    return { status: 'acquired', lock: buildLockState(nextRow) };
  }

  async refreshLock(reportId: string, owner: ReportLockOwner): Promise<boolean> {
    const row = await this.table.get(reportId);
    if (!row) return false;
    if (row.lockedDevice !== owner.deviceId) return false;

    const now = new Date().toISOString();
    const syncMeta = updateSyncMetadata(row as SyncMetadata);
    const nextRow: ReportRow = {
      ...row,
      ...syncMeta,
      lockedBy: owner.label ?? row.lockedBy ?? owner.deviceId,
      lockedAt: now,
      lockedDevice: owner.deviceId,
    };

    await this.table.put(nextRow);
    await queueForSync('reports', reportId, 'upsert', serializeReportRowForSync(nextRow));
    return true;
  }

  async releaseLock(reportId: string, owner: ReportLockOwner): Promise<void> {
    const row = await this.table.get(reportId);
    if (!row) return;
    if (row.lockedDevice && row.lockedDevice !== owner.deviceId) return;

    const syncMeta = updateSyncMetadata(row as SyncMetadata);
    const nextRow: ReportRow = {
      ...row,
      ...syncMeta,
      lockedBy: undefined,
      lockedAt: undefined,
      lockedDevice: undefined,
    };

    await this.table.put(nextRow);
    await queueForSync('reports', reportId, 'upsert', serializeReportRowForSync(nextRow));
  }

  async list(companyId?: string): Promise<ReportSummary[]> {
    const collection = companyId
      ? this.table.where('companyId').equals(companyId)
      : this.table.toCollection();
    const rows = await collection.toArray();
    // Filter out soft-deleted records
    const activeRows = rows.filter((row) => !this.isDeleted(row));
    return Promise.all(activeRows.map((entry) => this.toListModel(entry)));
  }

  async listBySite(siteId: string): Promise<ReportSummary[]> {
    const rows = await this.table.where('siteId').equals(siteId).toArray();
    // Filter out soft-deleted records
    const activeRows = rows.filter((row) => !this.isDeleted(row));
    return Promise.all(activeRows.map((entry) => this.toListModel(entry)));
  }

  async listDetailed(companyId?: string): Promise<Report[]> {
    const collection = companyId
      ? this.table.where('companyId').equals(companyId)
      : this.table.toCollection();
    const rows = await collection.toArray();
    // Filter out soft-deleted records
    const activeRows = rows.filter((row) => !this.isDeleted(row));
    return Promise.all(activeRows.map((row) => this.toGetModel(row)));
  }

  async getLatestFinalizedBySite(siteId: string): Promise<Report | null> {
    const summaries = await this.listBySite(siteId);
    const candidate = summaries
      .filter((summary) => summary.status === 'finalized')
      .sort((a, b) => {
        const aDate = a.visitDate ? new Date(a.visitDate).getTime() : 0;
        const bDate = b.visitDate ? new Date(b.visitDate).getTime() : 0;
        if (aDate === bDate) {
          const aUpdated = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const bUpdated = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return bUpdated - aUpdated;
        }
        return bDate - aDate;
      })[0];

    if (!candidate) return null;
    return this.get(candidate.id);
  }

  async getDraftBySite(siteId: string): Promise<Report | null> {
    const summaries = await this.listBySite(siteId);
    const candidate = summaries
      .filter((summary) => summary.status === 'draft')
      .sort((a, b) => {
        const aUpdated = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
        const bUpdated = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        return bUpdated - aUpdated;
      })[0];

    if (!candidate) return null;
    return this.get(candidate.id);
  }

  async getRecentFinalizedBySite(siteId: string, limit = 3): Promise<Report[]> {
    const summaries = await this.listBySite(siteId);
    const candidates = summaries
      .filter((summary) => summary.status === 'finalized')
      .sort((a, b) => {
        const aDate = a.visitDate ? new Date(a.visitDate).getTime() : 0;
        const bDate = b.visitDate ? new Date(b.visitDate).getTime() : 0;
        if (aDate === bDate) {
          const aUpdated = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const bUpdated = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return bUpdated - aUpdated;
        }
        return bDate - aDate;
      })
      .slice(0, limit);

    const reports = await Promise.all(candidates.map((entry) => this.get(entry.id)));

    return reports;
  }
}

export const ReportsRepo = new ReportsRepoImpl();
